import { Timestamp } from "firebase/firestore";
import { z } from "zod";

export const membershipSchema = z.object({
  uid: z.string(),
  eventId: z.string(),
  isEventAdmin: z.boolean().optional(),
  attendeeId: z.string().optional(),
  // The member's own track, mirrored from
  // their roster row by ASSIGN_ATTENDEE_TRACKS and `reconcile_member_track`.
  // Read here, from the document the member may already `get`, so the agenda
  // can be personalised without opening the attendee rows to members.
  // A malformed value reads as no track, never as "not a member".
  trackId: z.string().optional().catch(undefined),
  joinedAt: z.any().optional(),
});
export type Membership = z.infer<typeof membershipSchema>;

export const directoryAttendeeSchema = z.object({
  id: z.string(),
  name: z.string(),
  role: z.string(),
  company: z.string(),
  avatar: z.string().optional(),
  bio: z.string().optional(),
  linkedinUrl: z.string().optional(),
  trackId: z.string().optional(),
  // Present only when the person switched email sharing on in their profile;
  // the projection leaves it out otherwise (attendee_projection.py).
  email: z.string().optional(),
});
export type DirectoryAttendee = z.infer<typeof directoryAttendeeSchema>;

export const agendaItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  startsAt: z.any().optional(),
  endsAt: z.any().optional(),
  dayKey: z.string().optional(),
  description: z.string().optional(),
  trackIds: z.array(z.string()).optional(),
  presenterIds: z.array(z.string()).optional(),
  // Trigger-owned, and the only presenter data this view renders: clients
  // cannot write it (it is absent from the agenda-item key lists in the rules)
  // and cannot forge it. Exactly one of `attendeeId` and `speakerId` is set.
  // A speaker-backed entry is D14: a presenter who is not on the roster. Every
  // projection written before D14 carries `attendeeId`, so no agenda item has
  // to be rewritten for this to parse.
  presenters: z
    .array(
      z.object({
        attendeeId: z.string().optional(),
        speakerId: z.string().optional(),
        name: z.string(),
        avatar: z.string().optional(),
      }),
    )
    .optional(),
  // The subset of `presenterIds` the trigger would not put on the agenda: an
  // id matching no roster row and no speaker, or one whose roster row is
  // `withdrawn`, which means the person left the event and lost their
  // membership. The trigger reports them here and leaves `presenterIds`
  // alone, so the agenda editor can say a session has lost a presenter, and so
  // a person who re-registers goes back on their sessions by themselves.
  unresolvedPresenterIds: z.array(z.string()).optional(),
  roomName: z.string().optional(),
  locationNotes: z.string().optional(),
  location: z.string().optional(),
  // The bold landing page: an organiser marks up to three sessions as
  // highlights and may give them a shorter title and a blurb for the cards. A
  // malformed value reads as unset rather than dropping the session.
  highlight: z.boolean().nullable().optional().catch(undefined),
  highlightTitle: z.string().nullable().optional().catch(undefined),
  highlightBlurb: z.string().nullable().optional().catch(undefined),
  createdAt: z.any().optional(),
});
export type AgendaItem = z.infer<typeof agendaItemSchema>;

export const bookmarkSchema = z.object({
  eventId: z.string(),
  agendaItemId: z.string(),
  startsAt: z.instanceof(Timestamp),
  title: z.string(),
  createdAt: z.instanceof(Timestamp),
});
export type Bookmark = z.infer<typeof bookmarkSchema>;

export const trackSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  color: z.string().optional(),
  displayOrder: z.number().optional(),
  // What the "Your track" banner shows and what {room}, {question} and
  // {champion} fill in (TNG-95). The champion is a link to a roster attendee
  // or a speaker, at most one per champion, resolved to their current name when shown.
  room: z.string().optional().catch(undefined),
  question: z.string().optional().catch(undefined),
  championAttendeeId: z.string().optional().catch(undefined),
  championSpeakerId: z.string().optional().catch(undefined),
  // A second champion, linked the same way; tracks made before it have none.
  secondChampionAttendeeId: z.string().optional().catch(undefined),
  secondChampionSpeakerId: z.string().optional().catch(undefined),
  createdAt: z.any().optional(),
});
export type Track = z.infer<typeof trackSchema>;

export const speakerSchema = z.object({
  id: z.string(),
  name: z.string(),
  role: z.string().optional(),
  company: z.string().optional(),
  bio: z.string().optional(),
  avatar: z.string().optional(),
  // The roster row this speaker is the same person as. It is what resolves
  // their LinkedIn link and their signals on the speaker sheet, and what keeps
  // them off the agenda presenter picker a second time under a second id.
  attendeeId: z.string().optional(),
  displayOrder: z.number().optional(),
  createdAt: z.any().optional(),
});
export type Speaker = z.infer<typeof speakerSchema>;

export const partnerSchema = z.object({
  id: z.string(),
  name: z.string(),
  logo: z.string().optional(),
  description: z.string().optional(),
  websiteUrl: z.string().optional(),
  createdAt: z.any().optional(),
});
export type Partner = z.infer<typeof partnerSchema>;

export const venueSchema = z.object({
  id: z.string(),
  name: z.string(),
  address: z.string().optional(),
  description: z.string().optional(),
  location: z
    .object({
      latitude: z.number().min(-90).max(90),
      longitude: z.number().min(-180).max(180),
    })
    .optional(),
  floorPlanImages: z.array(z.string().min(1)).max(20).optional(),
  createdAt: z.any().optional(),
});
export type Venue = z.infer<typeof venueSchema>;

export const resourceSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  storagePath: z.string(),
  size: z.number().optional(),
  contentType: z.string().optional(),
  uploadedAt: z.any().optional(),
  uploadedBy: z.string().optional(),
  // Absent on resources written before track restriction; see
  // resource-audience.ts.
  audience: z.enum(["everyone", "tracks"]).optional(),
  trackIds: z.array(z.string()).optional(),
  // TNG-96: also listed in the "After the event" section. Absent means no.
  afterEvent: z.boolean().optional(),
});
export type Resource = z.infer<typeof resourceSchema>;

export const signalSchema = z.object({
  id: z.string(),
  label: z.string(),
  displayOrder: z.number().optional(),
  createdAt: z.any().optional(),
});
export type Signal = z.infer<typeof signalSchema>;

// Keyed by attendeeId so a directory card joins by document id. The array is
// ordered: the first id is the badge signal. Empty means the member chose to
// skip, which is a recorded answer rather than an absence.
export const signalSelectionSchema = z.object({
  id: z.string(),
  signalIds: z.array(z.string()).max(3),
  updatedAt: z.any().optional(),
});
export type SignalSelection = z.infer<typeof signalSelectionSchema>;

export const informationItemSchema = z.object({
  id: z.string(),
  kind: z.enum(["INFORMATION", "Q_AND_A"]),
  title: z.string(),
  body: z.string().optional(),
  displayOrder: z.number().optional(),
  isPublished: z.boolean().optional(),
  createdAt: z.any().optional(),
});
export type InformationItem = z.infer<typeof informationItemSchema>;
